import React from "react";
import { Microscope, FileCheck2, Snowflake, Shield, Beaker, Award, FlaskConical, Lock } from "lucide-react";
import { MolecularBg } from "../components/MolecularBg";
import { useSEO } from "../lib/useSEO";

const STANDARDS = [
    { i: Microscope, t: "HPLC Verification", d: "Every manufacturing batch undergoes reverse-phase high-performance liquid chromatography by the manufacturer to confirm peptide identity and purity. Minimum 98% threshold; most batches exceed 99%." },
    { i: FileCheck2, t: "Mass Spectrometry", d: "MS analysis by the manufacturer confirms exact peptide mass, sequence integrity, and rules out structural contaminants. Performed alongside HPLC on every manufacturing batch." },
    { i: Award, t: "Manufacturer-Supplied COA", d: "A manufacturer-supplied Certificate of Analysis is available for every product. The COA documents identity and purity results for the applicable manufacturing batch." },
    { i: Beaker, t: "Batch-Level COA Coverage", d: "A manufacturer-supplied Certificate of Analysis is available for every product. The COA represents the applicable manufacturing batch and is provided by the manufacturer for reference — it is not unique to each individual vial." },
    { i: Snowflake, t: "Cold-Chain Dispatch", d: "Insulated, temperature-monitored packaging with phase-change gel packs maintains 2–8°C from our Sydney facility to your door." },
    { i: Shield, t: "Tamper-Evident Sealing", d: "Each shipment is sealed with tamper-evident strips. Any sign of compromise on receipt should be reported within 48 hours for replacement." },
    { i: Lock, t: "Discreet Packaging", d: "Unmarked outer packaging with no Carbon Labs branding on the exterior. Privacy and discretion for every research order." },
    { i: FlaskConical, t: "Research-Use Only Compliance", d: "All products are sold strictly for research and laboratory use only. They are not for human consumption, diagnostic, or therapeutic use." },
];

export const LabStandardsPage = () => {
    useSEO({
        title: "Lab Standards — Carbon Labs Quality Protocols",
        description: "Manufacturer-supplied Certificates of Analysis documenting HPLC + MS results, cold-chain dispatch, and tamper-evident sealing. The Carbon Labs quality protocol.",
    });

    return (
        <div className="pt-24 pb-20 min-h-screen relative" data-testid="lab-standards-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10">
                <span className="eyebrow">// Quality Protocol</span>
                <h1 className="font-display italic text-4xl md:text-5xl font-black text-white tracking-tighter mt-1 mb-4 uppercase">
                    Lab Standards
                </h1>
                <p className="text-[var(--text-secondary)] mb-10 max-w-2xl leading-relaxed">
                    Every Carbon Labs research compound passes through the same eight quality gates before it
                    reaches your bench. No exceptions — if a batch fails any one, it never ships.
                </p>

                <div className="grid md:grid-cols-2 gap-3">
                    {STANDARDS.map((s, i) => (
                        <div key={i} className="glass-elevated rounded-2xl p-5 hover:border-[rgba(233,188,11,0.35)] transition-all" data-testid={`standard-card-${i}`}>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-lg bg-[rgba(233,188,11,0.1)] border border-[rgba(233,188,11,0.25)] flex items-center justify-center flex-shrink-0">
                                    <s.i size={18} className="text-[var(--gold)]" />
                                </div>
                                <h3 className="font-display italic uppercase font-bold text-white">{s.t}</h3>
                            </div>
                            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{s.d}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-10 glass-elevated rounded-3xl p-8 neon-border-cyan">
                    <h2 className="font-display italic uppercase text-2xl font-black text-white mb-3">Our purity guarantee</h2>
                    <p className="text-[var(--text-secondary)] leading-relaxed mb-3">
                        We refuse to ship any peptide below 98% purity, with most batches exceeding 99%. If a
                        manufacturing batch fails our QC review, it never reaches inventory. This is the standard
                        we'd demand if we were buying for our own bench — and it's the one we hold ourselves to.
                    </p>
                    <p className="text-xs font-mono uppercase tracking-widest text-[var(--gold)]">
                        Manufacturer-supplied Certificate of Analysis available for every product.
                    </p>
                </div>

                {/* Official COA scope statement */}
                <div
                    className="mt-6 rounded-2xl p-6 md:p-8 border border-[var(--gold)]/30 bg-gradient-to-br from-[rgba(233,188,11,0.06)] to-[rgba(233,188,11,0.02)]"
                    data-testid="coa-scope-statement"
                >
                    <h3 className="font-display italic uppercase text-lg font-bold text-[var(--gold)] mb-3 tracking-wide">
                        Certificate of Analysis — Scope
                    </h3>
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                        A manufacturer-supplied Certificate of Analysis (COA) is available for each product.
                        These documents are provided by the manufacturer and can be viewed by customers for
                        reference. The COA represents the applicable manufacturing batch and is not unique to
                        each individual vial.
                    </p>
                </div>
            </div>
        </div>
    );
};
